import { first } from 'rxjs/operators';
import { ContentService, Content } from '@app/_components/content';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ContentFormComponent } from '../content-form/content-form.component';
import { ContentGenerateComponent } from '../content-generate/content-generate.component';
import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertService, AuthenticationService } from '@app/_services';
import { LazyLoadEvent, MessageService, ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { DevelopmentService } from '@app/_components/development/development.service';
import * as moment from 'moment';

@Component({
    selector: 'app-content-list',
    templateUrl: './content-list.component.html',
    styleUrls: ['./content-list.component.css'],
    providers: [MessageService, ConfirmationService, DialogService, { provide: MAT_DIALOG_DATA, useValue: {} }],
    styles: [`
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
            position: -webkit-sticky;
            position: sticky;
            top: 0px;
        }
        @media screen and (max-width: 64em) {
            :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                top: 99px;
            }
        }
    `],
    standalone: false
})
export class ContentListComponent implements OnInit {
    constructor(
        private contentService: ContentService,
        private alertService: AlertService,
        private authService: AuthenticationService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private developmentService: DevelopmentService,
        private route: ActivatedRoute,
    ) {
        this.statuses = contentService.statuses;
        this.contentTypes = contentService.contentTypes;
    }

    @Input() showFilter = true;
    @Input() showPagination = true;
    contents: any;
    content: Content;

    first = 0;
    rows = 50;
    datasource: any;
    recordTotal: number;
    lazyLoadEvent: LazyLoadEvent;
    loading = true;
    multiSortMeta = [];
    formLoading = false;
    statuses: any;
    contentTypes: any;
    developments: any[] = [];
    selectedContents: any[] = [];
    bulkAction = '';

    columns: any[] = [
        { field: 'contentId', header: 'Id', width: '70px' },
        { field: 'contentTypeId', header: 'Type', width: '100px' },
        { field: 'title', header: 'Title', width: 'auto' },
        { field: 'status', header: 'Status', width: '100px' },
        { field: 'viewCount', header: 'Views', width: '80px' },
        { field: 'locationId', header: 'Location', width: '90px' },
        { field: 'developmentId', header: 'Development', width: '140px' },
        { field: 'publishDate', header: 'Published', width: '110px' },
        { field: 'aiGenerated', header: 'AI', width: '60px' },
    ];

    // Feature #14: Review Queue filter
    reviewQueueActive = false;

    // Feature #16: Pillar Page Generator
    showPillarDialog = false;
    pillarKeyword = '';
    pillarSourceGuides: any[] = [];
    pillarSelectedGuideIds: number[] = [];
    pillarLoading = false;
    publishedGuides: any[] = [];

    ngOnInit(): void {
        this.multiSortMeta = [];
        this.multiSortMeta.push({ field: 'createDate', order: 0 });
        this.loadDevelopments();
        const idParam = this.route.snapshot.paramMap.get('id');
        if (idParam) {
            const contentId = parseInt(idParam, 10);
            if (!isNaN(contentId)) {
                this.contentService.findById(contentId).pipe(first()).subscribe(
                    (res: any) => {
                        const content = res?.data?.content;
                        if (content) {
                            this.onOpenFormEdit(content);
                        } else {
                            this.messageService.add({ severity: 'warn', summary: 'Not Found', detail: 'Content #' + contentId + ' not found.', life: 4000 });
                        }
                    },
                    () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load content #' + contentId, life: 4000 }); }
                );
            }
        }
    }

    loadDevelopments() {
        this.developmentService.developmentSummaryList().pipe(first()).subscribe(
            (res: any) => {
                this.developments = (res.data.developmentSummaryList || []).map((d: any) => ({
                    ...d,
                    displayName: d.adrCounty ? `${d.addressDevelopmentName} (${d.adrCounty})` : d.addressDevelopmentName
                }));
            },
            () => {}
        );
    }

    getDevelopmentName(developmentId: number): string {
        if (!developmentId || !this.developments.length) return '';
        const dev = this.developments.find((d: any) => d.developmentId === developmentId);
        return dev ? dev.addressDevelopmentName : String(developmentId);
    }

    contentTypeName(id: number): string {
        if (!id || !this.contentTypes) return '';
        const ct = this.contentTypes.find((x: any) => x.contentTypeId === id);
        return ct ? ct.name : String(id);
    }

    onRowDelete(event: any) {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete content #' + event.contentId + '?',
            header: 'Confirm',
            icon: 'pi pi-exclamation-triangle',
            accept: () => { this.formDelete(event); },
            reject: () => { this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 }); }
        });
    }

    loadLazy(event: LazyLoadEvent) {
        try {
            this.loading = true;
            this.lazyLoadEvent = event;

            if (Object.keys(event.filters).length === 0 && !event.multiSortMeta) {
                event.multiSortMeta = [];
                event.multiSortMeta.push({ field: 'createDate', order: -1 });
            }

            const pageNumber = Math.round(event.first / event.rows);
            this.contentService.findByFilter(
                pageNumber, event.rows,
                event.sortField, event.sortOrder,
                event.multiSortMeta, event.globalFilter, event.filters
            ).pipe(first()).subscribe(
                (res: any) => {
                    this.recordTotal = res.page.recordTotal;
                    this.contents = res.data.contents;
                },
                (err: any) => {
                    this.alertService.error(err);
                },
                () => { this.loading = false; }
            );
        } catch (error) {
            console.log('loadLazy error: ' + error);
        }
    }

    fixDates(date: any) {
        return date ? moment(date).format('MM/DD/YYYY') : '';
    }

    onRowSelect(event: any) {
        this.onOpenFormEdit(event.data);
    }

    formDelete(content: any) {
        this.formLoading = true;
        this.contentService.delete(content.contentId).subscribe(
            (res: any) => {
                this.contents.splice(this.contents.indexOf(content), 1);
                this.contents = [...this.contents];
                this.recordTotal = this.contents.length;
                this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Content #' + content.contentId + ' deleted.', life: 3000 });
            },
            (err: any) => { this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: err.message, life: 3000 }); },
            () => { this.formLoading = false; }
        );
    }

    onOpenFormAdd() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '900px';
        dialogConfig.maxHeight = '90vh';
        dialogConfig.data = { title: 'Add Content' };

        const dialogRef = this.dialog.open(ContentFormComponent, dialogConfig);
        dialogRef.componentInstance.doAdd.subscribe(data => {
            this.addContentCB(data);
            dialogRef.close();
        });
    }

    onGenerateContent() {
        const currentUser: any = this.authService.currentUserValue;
        const ref = this.dialogService.open(ContentGenerateComponent, {
            header: 'AI Content Generator',
            width: 'min(900px, 96vw)',
            contentStyle: { overflow: 'hidden', padding: '0' },
            baseZIndex: 11000,
            data: { bearerToken: currentUser?.token }
        });
        ref.onClose.subscribe((result: any) => {
            if (result) {
                this.addContentCB(result);
            }
        });
    }

    onOpenFormEdit(content: Content) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '900px';
        dialogConfig.maxHeight = '90vh';
        dialogConfig.data = { content: content, title: 'Edit Content' };

        const dialogRef = this.dialog.open(ContentFormComponent, dialogConfig);
        dialogRef.componentInstance.doUpdate.subscribe(data => {
            this.updateContentCB(data);
            dialogRef.close();
        });
        dialogRef.componentInstance.doDelete.subscribe(data => {
            this.deleteContentCB(data);
            dialogRef.close();
        });
    }

    addContentCB(content: Content) {
        if (!this.contents) this.contents = [];
        this.contents.unshift(content);
        this.contents = [...this.contents];
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Content #' + content.contentId + ' added.', life: 3000 });
    }

    updateContentCB(content: Content) {
        let idx = this.contents.findIndex((c: any) => c.contentId === content.contentId);
        if (idx >= 0) {
            this.contents[idx] = content;
        } else {
            // Item not found in current page — prepend it so the user sees
            // the updated record immediately regardless of current pagination.
            this.contents.unshift(content);
        }
        this.contents = [...this.contents];
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Content #' + content.contentId + ' updated.', life: 3000 });
    }

    deleteContentCB(content: Content) {
        this.contents.splice(this.contents.indexOf(content), 1);
        this.contents = [...this.contents];
        this.recordTotal = this.contents.length;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Content #' + content.contentId + ' deleted.', life: 3000 });
    }

    applyBulkAction(): void {
        if (!this.bulkAction || !this.selectedContents?.length) return;
        const ids = this.selectedContents.map(c => c.contentId);
        const newStatus = this.bulkAction;
        let remaining = ids.length;
        let errors = 0;
        ids.forEach(id => {
            const content = this.contents.find((c: any) => c.contentId === id);
            if (!content) { remaining--; return; }
            const payload = { ...content, status: newStatus };
            this.contentService.update(payload).pipe(first()).subscribe(
                (res: any) => {
                    const updated = res?.data?.content;
                    if (updated) {
                        const idx = this.contents.findIndex((c: any) => c.contentId === id);
                        if (idx >= 0) this.contents[idx] = updated;
                    }
                },
                () => { errors++; },
                () => {
                    remaining--;
                    if (remaining === 0) {
                        this.contents = [...this.contents];
                        this.selectedContents = [];
                        this.bulkAction = '';
                        const msg = errors > 0
                            ? `Bulk update complete. ${errors} error(s).`
                            : `${ids.length} item(s) set to ${newStatus}.`;
                        this.messageService.add({ severity: errors > 0 ? 'warn' : 'success', summary: 'Bulk Update', detail: msg, life: 4000 });
                    }
                }
            );
        });
    }

    // Feature #14: Review Queue
    toggleReviewQueue(pt: any): void {
        this.reviewQueueActive = !this.reviewQueueActive;
        if (this.reviewQueueActive) {
            pt.filter('REVIEW', 'status', 'equals');
        } else {
            pt.filter(null, 'status', 'equals');
        }
    }

    // Feature #16: Pillar Page Generator
    openPillarDialog(): void {
        this.pillarKeyword = '';
        this.pillarSelectedGuideIds = [];
        this.pillarSourceGuides = [];
        this.showPillarDialog = true;
        this.loadPublishedGuides();
    }

    loadPublishedGuides(): void {
        this.contentService.getByContentType('GUIDE').pipe(first()).subscribe(
            (res: any) => {
                this.publishedGuides = (res.data.contents || []).filter((c: any) => c.status === 'PUBLISHED');
            },
            () => {}
        );
    }

    generatePillarPage(): void {
        if (!this.pillarKeyword || this.pillarSelectedGuideIds.length < 1) return;
        this.pillarLoading = true;
        this.contentService.generatePillarPage(this.pillarKeyword, this.pillarSelectedGuideIds).pipe(first()).subscribe(
            (res: any) => {
                const created = res.data.content;
                if (created) {
                    this.addContentCB(created);
                    this.messageService.add({ severity: 'success', summary: 'Pillar Page Created', detail: `Draft created: ${created.title}`, life: 5000 });
                }
                this.showPillarDialog = false;
            },
            (err: any) => {
                this.messageService.add({ severity: 'error', summary: 'Generation Failed', detail: err.message, life: 4000 });
            },
            () => { this.pillarLoading = false; }
        );
    }

    next() { this.first = this.first + this.rows; }
    prev() { this.first = this.first - this.rows; }
    reset() { this.first = 0; }
    isLastPage(): boolean { return this.contents ? this.first === (this.contents.length - this.rows) : true; }
    isFirstPage(): boolean { return this.contents ? this.first === 0 : true; }

    reloadPage() { window.location.reload(); }

    regenMeta(): void {
        this.contentService.regenMissingMeta().subscribe({
            next: (res: any) => {
                const count = res?.updated ?? res?.data?.updated ?? 0;
                this.messageService.add({ severity: 'success', summary: 'Meta Regen', detail: `Updated ${count} content item(s)` });
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Meta Regen', detail: 'Failed to run meta regen' });
            }
        });
    }
}
